import { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import useSWR from 'swr';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Pagination from 'components/Pagination/Pagination';
import {
  IDepositExtended,
  SortOrder,
  TransactionSortField,
} from 'views/types/typeTransaction';
import { txsService } from 'views/services/txsService';
import { User } from 'views/types/typeUser';
import { formatUnits } from 'viem';
import {
  EOrderCurrency,
  EOrderStatus,
  EOrderType,
  ORDER_STATUS_OPTIONS,
  SHOW_OPERATION_DIRECTION_TYPES,
} from 'views/types/transactions';
import { statusToText } from 'utils/status-to-text';
import { useDebounceValue } from 'usehooks-ts';
import { useTransactionFilterStore } from 'views/store/useUserTxsTableStore';
import DatePicker from 'react-datepicker';
import { EDatePresets } from 'views/types/date-range-preset.enum';
import dayjs from 'dayjs';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import clsx from 'clsx';

interface Paginated<T> {
  data: T[];
  meta: {
    totalItems: number;
    totalPages: number;
  };
}

interface IProps {
  user: User;
  onTransactionClick?: (transaction: IDepositExtended) => void;
}

interface SortState {
  sortBy: TransactionSortField | null;
  sortOrder: SortOrder | null;
}

const columnHelper = createColumnHelper<IDepositExtended>();

const getColumns = (
  sortState: SortState,
  handleSort: (field: TransactionSortField) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<IDepositExtended, any>[] => [
  columnHelper.accessor('id', {
    header: 'Operation ID',
    cell: info => {
      const value = info.getValue();
      if (!value) return null;

      const shortened = `${value.slice(0, 8)}...${value.slice(-6)}`;

      return (
        <span title={value} className="font-medium font-mono text-sm">
          <span className="hidden lg:inline">{value}</span>
          <span className="inline lg:hidden">{shortened}</span>
        </span>
      );
    },
  }),
  columnHelper.accessor('type', {
    header: () => (
      <div
        className="cursor-pointer flex items-center gap-2"
        onClick={() => handleSort('type')}
      >
        Type
        {sortState.sortBy === 'type' ? (
          sortState.sortOrder === 'DESC' ? (
            <FaSortDown className="text-[10px]" />
          ) : (
            <FaSortUp className="text-[10px]" />
          )
        ) : (
          <FaSort className="text-[10px]" />
        )}
      </div>
    ),
    cell: info => {
      let typeValue = info.getValue();

      if (typeValue && typeValue === 'internal') {
        const isPositive = +info.row.original.amountIn > 0;
        typeValue = isPositive
          ? typeValue + ' deposit'
          : typeValue + ' withdraw';
      }

      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {typeValue}
        </span>
      );
    },
  }),
  columnHelper.accessor('createdAt', {
    header: () => (
      <div
        className="cursor-pointer flex items-center gap-2"
        onClick={() => handleSort('createdAt')}
      >
        Created at
        {sortState.sortBy === 'createdAt' ? (
          sortState.sortOrder === 'DESC' ? (
            <FaSortDown className="text-[10px]" />
          ) : (
            <FaSortUp className="text-[10px]" />
          )
        ) : (
          <FaSort className="text-[10px]" />
        )}
      </div>
    ),
    cell: info => (
      <span className="text-sm">
        {new Date(info.getValue()).toLocaleString('uk-UA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </span>
    ),
  }),
  columnHelper.accessor('amountIn', {
    header: 'Debit/Credit',
    cell: info => {
      const transaction = info.row.original;
      const amountRaw = info.getValue();
      const totalFee = (+transaction.fee || 0) + (+transaction.amlFee || 0);
      const currency = transaction.currencyIn || transaction.currency;
      const feeCurrency =
        transaction.type === 'swap'
          ? transaction?.currencyOut || transaction?.currency
          : transaction?.currencyIn || transaction?.currency;

      const currencySymbol = currency?.symbol || 'N/A';
      const feeCurrencySymbol = feeCurrency?.symbol || 'N/A';
      const decimals = currency?.decimals || 0;
      const feeDecimals = feeCurrency?.decimals || 0;
      const typeValue = info.row.original.type as EOrderType;

      const isPositive = +amountRaw > 0;

      const isCredit =
        [
          EOrderType.DEPOSIT,
          EOrderType.CARD_TOP_UP,
          EOrderType.REFERRAL_TRANSACTION,
        ].includes(typeValue) ||
        ([EOrderType.CARD_TRANSACTION, EOrderType.INTERNAL].includes(
          typeValue
        ) &&
          isPositive);

      const isAmlCheck =
        typeValue === EOrderType.AML_WALLET_CHECK ||
        typeValue === EOrderType.AML_TX_CHECK;

      let formattedFee: string;
      try {
        const feeValue = isAmlCheck ? +amountRaw : Math.abs(totalFee);
        const feeInSmallestUnits = !isAmlCheck
          ? BigInt(Math.round(feeValue * 10 ** feeDecimals))
          : BigInt(Math.round(feeValue));
        formattedFee = formatUnits(feeInSmallestUnits, feeDecimals);
      } catch (error) {
        console.error('Error formatting fee:', error);
        formattedFee = 'Invalid fee';
      }

      let formattedAmount: string | null = null;
      if (!isAmlCheck) {
        try {
          formattedAmount = formatUnits(BigInt(Math.abs(amountRaw)), decimals);
        } catch (error) {
          console.error('Error formatting amount:', error);
          formattedAmount = 'Invalid amount';
        }
      }

      return (
        <div className="flex flex-col">
          {SHOW_OPERATION_DIRECTION_TYPES.includes(typeValue) ? (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                !isCredit
                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              }`}
            >
              {!isCredit ? 'Debit' : 'Credit'}
            </span>
          ) : (
            <div className="flex opacity-0">-</div>
          )}
          {!isAmlCheck && formattedAmount && (
            <span className="text-xs text-gray-600 mt-1">
              Amt. - {formattedAmount} {currencySymbol}
            </span>
          )}
          {Number(formattedFee) > 0 && (
            <span className="text-xs text-gray-600 mt-1">
              Fee - {formattedFee} {feeCurrencySymbol}
            </span>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => {
      const status = statusToText(info.getValue(), info.row.original.type);

      const getStatusStyles = (status: string) => {
        switch (status) {
          case 'Completed':
            return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
          case 'Pending':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
          case 'Failed':
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
          default:
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
        }
      };

      const capitalize = (text: string) =>
        text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

      return (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyles(status)}`}
        >
          {capitalize(status)}
        </span>
      );
    },
  }),
];

export const UserTxsTable = ({ user, onTransactionClick }: IProps) => {
  const [_, setPreset] = useState<EDatePresets>(EDatePresets.ALL);
  const { canRead, canUpdate } = usePermissions();
  const [[startDate, endDate], setDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const applyPreset = (p: EDatePresets) => {
    setPreset(p);
    let from: Date | null = null;
    let to: Date | null = null;
    switch (p) {
      case EDatePresets.DAY:
        from = dayjs().startOf('day').toDate();
        to = dayjs().endOf('day').toDate();
        break;
      case EDatePresets.WEEK:
        from = dayjs().startOf('week').toDate();
        to = dayjs().endOf('week').toDate();
        break;
      case EDatePresets.MONTH:
        from = dayjs().startOf('month').toDate();
        to = dayjs().endOf('month').toDate();
        break;
      case EDatePresets.ALL:
      default:
        break;
    }
    setDateRange([from, to]);
    setPage(0);
  };
  const {
    page,
    setPage,
    txType,
    setTxType,
    searchBy,
    setSearchBy,
    itemsPerPage,
    setItemsPerPage,
    sortState,
    setSortState,
  } = useTransactionFilterStore();
  const [currencyFilter, setCurrencyFilter] = useState<EOrderCurrency>(
    EOrderCurrency.All
  );
  const [statusFilter, setStatusFilter] = useState<EOrderStatus>(
    EOrderStatus.ALL
  );

  const [searchDebounce] = useDebounceValue(searchBy, 500);

  const { data, isLoading } = useSWR<Paginated<IDepositExtended>>(
    [
      '/api/orders/all-transactions',
      {
        page: page + 1,
        limit: itemsPerPage,
        search: user.id,
        searchBy: searchDebounce || undefined,
        txType: txType !== EOrderType.ALL ? txType : undefined,
        currency:
          currencyFilter !== EOrderCurrency.All ? currencyFilter : undefined,
        status: statusFilter !== EOrderStatus.ALL ? statusFilter : undefined,
        ...(sortState.sortBy && { sortBy: sortState.sortBy }),
        ...(sortState.sortOrder && { sortOrder: sortState.sortOrder }),
        ...(startDate && {
          from: dayjs(startDate).startOf('day').toISOString(),
        }),
        ...(endDate && { to: dayjs(endDate).endOf('day').toISOString() }),
      },
    ],
    ([_, params]) => txsService.getAllTransactions(params),
    { refreshInterval: 10000 }
  );

  const handleSort = (field: TransactionSortField) => {
    if (sortState.sortBy === field) {
      if (sortState.sortOrder === 'ASC') {
        setSortState({ sortBy: field, sortOrder: 'DESC' });
      } else if (sortState.sortOrder === 'DESC') {
        setSortState({ sortBy: null, sortOrder: null });
      } else {
        setSortState({ sortBy: field, sortOrder: 'ASC' });
      }
    } else {
      setSortState({ sortBy: field, sortOrder: 'ASC' });
    }

    setPage(0);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => getColumns(sortState, handleSort), [sortState]);

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  useEffect(() => {
    const savedStartDate = sessionStorage.getItem('startDate');
    const savedEndDate = sessionStorage.getItem('endDate');
    const savedCurrencyFilter = sessionStorage.getItem('currencyFilter');
    const savedStatusFilter = sessionStorage.getItem('statusFilter');

    if (savedStartDate && savedEndDate) {
      setDateRange([new Date(savedStartDate), new Date(savedEndDate)]);
    }

    if (savedCurrencyFilter) {
      setCurrencyFilter(savedCurrencyFilter as EOrderCurrency);
    }
    if (savedStatusFilter) {
      setStatusFilter(savedStatusFilter as EOrderStatus);
    }
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      sessionStorage.setItem('startDate', startDate.toISOString());
      sessionStorage.setItem('endDate', endDate.toISOString());
    }
    sessionStorage.setItem('currencyFilter', currencyFilter);
    sessionStorage.setItem('statusFilter', statusFilter);
  }, [currencyFilter, statusFilter, startDate, endDate]);

  return (
    <>
      <div className="flex relative flex-wrap md:flex-row md:items-center gap-4 items-center pb-3 w-full h-full justify-between">
        <div className="flex flex-wrap gap-2 md:gap-3 items-center">
          <select
            disabled={!canUpdate(PERMISSION_RESOURCES.TRANSACTIONS)}
            value={txType}
            onChange={e => {
              setTxType(e.target.value as EOrderType);
              setPage(0);
            }}
            className={clsx(
              'rounded-lg border p-2 bg-white dark:bg-navy-900 text-navy-700 dark:text-white transition-colors',
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS) &&
                'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            )}
          >
            <option value={EOrderType.ALL}>All Transactions</option>
            <option value={EOrderType.DEPOSIT}>Deposits</option>
            <option value={EOrderType.WITHDRAW}>Withdrawals</option>
            <option value={EOrderType.SWAP}>Swaps</option>
            <option value={EOrderType.INTERNAL}>Internal</option>
            <option value={EOrderType.CARD_ISSUE}>Card Issue</option>
            <option value={EOrderType.CARD_TOP_UP}>Card Top Up</option>
            <option value={EOrderType.CARD_WITHDRAW}>Card Withdraw</option>
            <option value={EOrderType.CARD_TRANSACTION}>
              Card Transactions
            </option>
            <option value={EOrderType.REFERRAL_TRANSACTION}>
              Referral Transactions
            </option>
            <option value={EOrderType.AML_WALLET_CHECK}>
              AML Wallet Check
            </option>
            <option value={EOrderType.AML_TX_CHECK}>AML TX Check</option>
          </select>

          <select
            disabled={!canUpdate(PERMISSION_RESOURCES.TRANSACTIONS)}
            value={currencyFilter}
            onChange={e => {
              setCurrencyFilter(e.target.value as EOrderCurrency);
              setPage(0);
            }}
            className={clsx(
              'rounded-lg border p-2 bg-white dark:bg-navy-900 text-navy-700 dark:text-white transition-colors',
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS) &&
                'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            )}
          >
            <option value={EOrderCurrency.All}>All Currencies</option>
            <option value={EOrderCurrency.USDT}>USDT</option>
            <option value={EOrderCurrency.USDC}>USDC</option>
            <option value={EOrderCurrency.TRX}>TRX</option>
            <option value={EOrderCurrency.SOL}>SOL</option>
            <option value={EOrderCurrency.BTC}>BTC</option>
            <option value={EOrderCurrency.ETH}>ETH</option>
          </select>

          <select
            disabled={!canUpdate(PERMISSION_RESOURCES.TRANSACTIONS)}
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value as EOrderStatus);
              setPage(0);
            }}
            className={clsx(
              'rounded-lg border p-2 bg-white dark:bg-navy-900 text-navy-700 dark:text-white transition-colors',
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS) &&
                'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            )}
          >
            <option value={EOrderStatus.ALL}>All statuses</option>
            {ORDER_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <DatePicker
            disabled={!canUpdate(PERMISSION_RESOURCES.TRANSACTIONS)}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={dates => {
              setPreset(EDatePresets.ALL);
              const [rawFrom, rawTo] = dates as [Date, Date];
              const from = rawFrom
                ? dayjs(rawFrom).startOf('day').toDate()
                : null;
              const to = rawTo ? dayjs(rawTo).endOf('day').toDate() : null;
              setDateRange([from, to]);
              setPage(0);
            }}
            isClearable
            placeholderText="Custom range…"
            className={clsx(
              'rounded border px-2 py-1 bg-white dark:bg-navy-900 text-navy-700 dark:text-white transition-colors',
              !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS) &&
                'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            )}
            popperClassName="react-datepicker-popper"
          >
            <div className="flex gap-2 p-2 border-t bg-white">
              <button
                className="px-2 py-1 bg-gray-100 rounded"
                onClick={() => applyPreset(EDatePresets.DAY)}
              >
                Today
              </button>
              <button
                className="px-2 py-1 bg-gray-100 rounded"
                onClick={() => applyPreset(EDatePresets.WEEK)}
              >
                This Week
              </button>
              <button
                className="px-2 py-1 bg-gray-100 rounded"
                onClick={() => applyPreset(EDatePresets.MONTH)}
              >
                This Month
              </button>
            </div>
          </DatePicker>
        </div>
        <input
          disabled={!canUpdate(PERMISSION_RESOURCES.TRANSACTIONS)}
          type="text"
          placeholder="Search by Transaction ID, Client ID, Email, TG Name..."
          className={clsx(
            'rounded-full w-[12.7rem] bg-lightPrimary px-4 py-2 text-base text-navy-700 dark:bg-navy-900 dark:text-white transition-colors',
            !canUpdate(PERMISSION_RESOURCES.TRANSACTIONS) &&
              'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
          )}
          value={searchBy}
          onChange={e => {
            setSearchBy(e.target.value);
            setPage(0);
          }}
        />
      </div>
      {isLoading ? (
        <span>Loading...</span>
      ) : !data || data.data.length === 0 ? (
        <div className="text-center py-8">
          <div className="font-semibold text-gray-500">
            No transactions found
          </div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="bg-white pb-2 px-4 pt-4 text-start dark:bg-navy-800"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => {
                      if (
                        canRead(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE)
                      ) {
                        onTransactionClick?.(row.original);
                      }
                    }}
                    className={`transition-colors duration-200 [&_td]:odd:bg-gray-50 [&_td]:odd:dark:bg-navy-900 ${
                      canRead(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE)
                        ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700'
                        : 'cursor-not-allowed opacity-60'
                    }`}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="p-2 px-4">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={data?.meta?.totalPages}
            totalItems={data?.meta?.totalItems}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            isBottomIndicators
          />
        </>
      )}
    </>
  );
};
